import React, { useState, useMemo, useRef, useEffect } from 'react';
import MoneyInput from './money-input';
import AutoCompleteField from './field-auto-complete';
import FormAsyncSelect from './form-async-select';
import Utils from '../utils/utils';
import AccessControl from '../utils/access_control';
import { fetch } from '../utils/api-wrapper';
import { cachedFetch } from '../utils/cached-api-wrapper';
import { resolveDefaultTaxCode } from '../utils/tax-code-resolvers';
import AsyncSelect from 'react-select/async';
import { inject, observer } from "mobx-react";
import Modal from './modal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faPlus,
    faWindowClose,
    faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import Switch from "react-switch";
import Select from 'react-select';
import GlSegmentSelect from './gl-segment-select';
import { isGlSegmentActive } from '../utils/additional-fields-filter-helpers';
import { buildPaymentTermsOptions, defaultPaymentTerm } from '../utils/payment-terms-options';

// Matches Bootstrap form-control height (calc(1.5em + .75rem + 2px)) so that
// React-select controls scale with font-size the same way plain inputs do.
const supplierSelectStyle = {
    ...Utils.styleSelect,
    control: (base, state) => ({
        ...Utils.styleSelect.control(base, state),
        minHeight: 'calc(1.5em + .75rem + 2px)',
        height: 'calc(1.5em + .75rem + 2px)',
    }),
    valueContainer: (base) => ({
        ...base,
        padding: '0 8px',
    }),
    indicatorsContainer: (base) => ({
        ...base,
        height: 'calc(1.5em + .75rem + 2px)',
    }),
};

// Validation tooltip wrapper — no browser APIs, no proxy inputs.
// Wraps any field and shows `error` as a tooltip when it is a non-empty string.
// Pass any message: "Please fill in this field.", "Minimum 7 digits.", etc.
const ValidatedField = ({ name, error, className, children, clearError }) => (
    <div data-field={name} className={className} style={{ position: 'relative' }} onClickCapture={() => clearError?.(name)}>
        {children}
        {error && (
            <>
                <div style={{
                    position: 'absolute',
                    top: 'calc(100% + 6px)',
                    left: 14,
                    width: 0,
                    height: 0,
                    borderLeft: '7px solid transparent',
                    borderRight: '7px solid transparent',
                    borderBottom: '7px solid #ccc',
                    zIndex: 1001,
                    pointerEvents: 'none',
                }} />
                <div style={{
                    position: 'absolute',
                    top: 'calc(100% + 4px)',
                    left: 16,
                    width: 0,
                    height: 0,
                    borderLeft: '6px solid transparent',
                    borderRight: '6px solid transparent',
                    borderBottom: '6px solid #fff',
                    zIndex: 1002,
                    pointerEvents: 'none',
                }} />
                <div style={{
                    position: 'absolute',
                    top: 'calc(100% + 10px)',
                    left: 0,
                    zIndex: 1000,
                    background: '#fff',
                    color: '#333',
                    border: '1px solid #ccc',
                    borderRadius: '6px',
                    padding: '8px 12px',
                    fontSize: '13px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    pointerEvents: 'none',
                    whiteSpace: 'nowrap',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                }}>
                    <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '20px',
                        height: '20px',
                        background: '#e8720c',
                        borderRadius: '4px',
                        color: '#fff',
                        fontWeight: 'bold',
                        fontSize: '13px',
                        flexShrink: 0,
                    }}>!</span>
                    {error}
                </div>
            </>
        )}
    </div>
);

// Field Components
const EntityTypeField = ({ data, updatedSupplier, setUpdatedSupplier, entityOptions }) => {
    if (!Utils.isExportFormat(data, "elite")) return null;
    
    return (
        <div className='d-flex align-items-center'>
            <div className='col-2 pl-1'>
                {data.t.read("supplier_table.entity_type")}
            </div>
            <div className='col-3'>
                <Select
                    name="entity_type"
                    value={entityOptions.find(option => option.value === updatedSupplier?.entity_type) ?? entityOptions[0]}
                    onChange={choice => setUpdatedSupplier({
                        ...updatedSupplier,
                        entity_type: choice.value
                    })}
                    styles={supplierSelectStyle}
                    classNamePrefix="select"
                    options={entityOptions} />
            </div>
        </div>
    );
};

const NamesField = ({ data, updatedSupplier, setUpdatedSupplier, nameRefs, invalidFields, clearError }) => (
    <div className="form-group">
        <h5 className='mt-4'>
            {data.t.read("supplier_table.names")} <span style={{ color: '#dc3545', fontSize: '12px' }}>*</span>
            <button className='btn btn-primary btn-xs ml-2 pl-1 pr-1' type="button"
                onClick={() => setUpdatedSupplier({
                    ...updatedSupplier,
                    names: [...updatedSupplier?.names ?? [], ""]
                })}>
                <FontAwesomeIcon icon={faPlus} />
            </button>
        </h5>
        <ValidatedField name="names" error={invalidFields?.names} clearError={clearError} />
        {updatedSupplier.names?.map((name, i) => (
            <ValidatedField key={i} name={`names.${i}`} error={invalidFields?.[`names.${i}`]} clearError={clearError} required>
                <div className="d-flex justify-content-between form-control align-items-center mb-2 p-2">
                    <input
                        ref={(el) => (nameRefs.current[i] = el)}
                        placeholder={`${data.t.read("name")} *`}
                        minLength={1}
                        type="text" className="form-control border-0" name="name"
                        value={name} style={{ backgroundColor: 'transparent' }}
                        onChange={(event) => {
                            clearError?.(`names.${i}`);
                            setUpdatedSupplier({
                                ...updatedSupplier,
                                names: updatedSupplier.names.map((item, index) => index === i ? event.target.value : item)
                            });
                        }} />
                    <FontAwesomeIcon icon={faWindowClose} type="button" color={"red"}
                        onClick={() => setUpdatedSupplier({
                            ...updatedSupplier,
                            names: updatedSupplier.names.filter((_, index) => index !== i)
                        })} />
                </div>
            </ValidatedField>
        ))}
    </div>
);

// XPEN-3620: tenants whose GL accounts and cost types are loaded in XPENS pick
// them from the search lists, so the codes that reach the accounting system are
// the ones it knows.
const picksCodesFromReferenceData = (data) => Utils.isExportFormat(data, "elite")
    || Utils.isExportFormat(data, "juris_format")
    || Utils.isExportFormat(data, "juris_lexis_nexis");

const CodesField = ({ data, updatedSupplier, setUpdatedSupplier, supplierNumberRef, defaultApGlNat, defaultExpenseGlNat, isCreateMode, invalidFields, clearError }) => (
    <div className="form-group">
        <h5 className='mt-4'>Codes</h5>
        <div className=' d-flex flex-wrap mt-1'>
            <ValidatedField name="supplier_number" error={invalidFields?.supplier_number} className="col-4 mb-2 px-0" clearError={clearError}>
                <input
                    ref={supplierNumberRef}
                    readOnly={Utils.isExportFormat(data, "juris_format") || !!data.config?.auto_supplier_number}
                    placeholder={(!Utils.isExportFormat(data, "juris_format") && !data.config?.auto_supplier_number) ? `${data.t.read("supplier_table.supplier_number")} *` : data.t.read("supplier_table.supplier_number")}
                    className='form-control border-0'
                    type="text"
                    value={updatedSupplier.supplier_number ?? ""}
                    onChange={e => {
                        clearError?.('supplier_number');
                        setUpdatedSupplier({ ...updatedSupplier, supplier_number: e.target.value });
                    }} />
            </ValidatedField>

            <ValidatedField name="gl_code" error={invalidFields?.gl_code} className="col-4 mb-2" clearError={clearError}>
                {picksCodesFromReferenceData(data) ?
                    <FormAsyncSelect
                        searchUrl={"/gl/nat/search"}
                        placeholder={`${data.t.read("gl_codes")}${!updatedSupplier.IsBillable ? ' *' : ''}`}
                        value={updatedSupplier.gl_code ?? null}
                        onChange={(val) => {
                            clearError?.('gl_code');
                            setUpdatedSupplier({ ...updatedSupplier, gl_code: val?.value ?? null });
                        }}
                    /> :
                    <AutoCompleteField
                        searchUrl={"/gl/nat/search/"}
                        placeholder={`${data.t.read("gl_codes")}${!updatedSupplier.IsBillable ? ' *' : ''}`}
                        value={updatedSupplier.gl_code ?? ""}
                        onChange={(val) => {
                            clearError?.('gl_code');
                            setUpdatedSupplier({ ...updatedSupplier, gl_code: val?.name ?? val ?? null });
                        }}
                        className="form-control"
                        minLength={1}
                    />
                }
            </ValidatedField>

            {Utils.isEngineType(data, "legal") &&
                <ValidatedField name="cost_type" error={invalidFields?.cost_type} className="col-4 mb-2" clearError={clearError}>
                    {picksCodesFromReferenceData(data) ?
                        <FormAsyncSelect
                            searchUrl={"/cost_type/search"}
                            placeholder={`${data.t.read("supplier_table.cost_type")}${updatedSupplier.IsBillable ? ' *' : ''}`}
                            value={updatedSupplier.cost_type ?? null}
                            onChange={(val) => {
                                clearError?.('cost_type');
                                setUpdatedSupplier({ ...updatedSupplier, cost_type: val?.value ?? null });
                            }}
                        /> :
                        <AutoCompleteField
                            searchUrl={"/cost_type/search/"}
                            placeholder={`${data.t.read("supplier_table.cost_type")}${updatedSupplier.IsBillable ? ' *' : ''}`}
                            value={updatedSupplier.cost_type ?? ""}
                            onChange={(val) => {
                                clearError?.('cost_type');
                                setUpdatedSupplier({ ...updatedSupplier, cost_type: val?.name ?? val ?? null });
                            }}
                            className="form-control"
                            minLength={1}
                        />
                    }
                </ValidatedField>}

            {Utils.isExportFormat(data, "elite") &&
                <>
                    <ValidatedField name="vendor_number" error={invalidFields?.vendor_number} className="col-4 mb-2 px-0" clearError={clearError}>
                        <input
                            placeholder={(isCreateMode && !data.config?.auto_supplier_number) ? `${data.t.read("supplier_table.vendor_number")} *` : data.t.read("supplier_table.vendor_number")}
                            className='form-control border-0'
                            type="text"
                            readOnly={!isCreateMode || !!data.config?.auto_supplier_number}
                            value={updatedSupplier.vendor_number ?? ""}
                            onChange={e => {
                                clearError?.('vendor_number');
                                setUpdatedSupplier({ ...updatedSupplier, vendor_number: e.target.value });
                            }}
                        />
                    </ValidatedField>


                    <div className='col-4 mb-2'>
                        <FormAsyncSelect
                            searchUrl={"/gl/nat/search"}
                            placeholder={data.t.read("supplier_table.ap_gl_natural")}
                            value={updatedSupplier.ap_gl_nat ?? defaultApGlNat ?? null}
                            isDisabled={true}
                        />
                    </div>
                    {!data.config?.hide_supplier_expense_gl &&
                        <ValidatedField name="expense_gl_nat" error={invalidFields?.expense_gl_nat} className="col-4 mb-2" clearError={clearError}>
                            <FormAsyncSelect
                                searchUrl={"/gl/nat/search"}
                                placeholder={`${data.t.read("supplier_table.ap_gl_expense")} *`}
                                value={updatedSupplier.expense_gl_nat ?? defaultExpenseGlNat ?? null}
                                onChange={(val) => {
                                    clearError?.('expense_gl_nat');
                                    setUpdatedSupplier({ ...updatedSupplier, expense_gl_nat: val?.value ?? null });
                                }}
                            />
                        </ValidatedField>}
                </>}
        </div>
    </div>
);

const AddressField = ({ data, updatedSupplier, setUpdatedSupplier, handleAddressChange, streetRef, cityRef, zipcodeRef, stateRef, countryRef, invalidFields, clearError }) => (
    <div className='form-group'>
        <h5 className='mt-4'>
            {data.t.read("supplier_table.address")}
        </h5>
        <ValidatedField name="address" error={invalidFields?.address} clearError={clearError}>
            <AutoCompleteField
                value={updatedSupplier.address ?? ""}
                onChange={(val) => {
                    clearError?.('address');
                    handleAddressChange(val);
                }}
                className="form-control"
                searchUrl="/places/search/"
                placeholder={data.t.read("supplier_table.address")}
            />
        </ValidatedField>

        <div className='mt-2 d-flex justify-content-between'>
            <ValidatedField name="street" error={invalidFields?.street} className="col-4 px-0" clearError={clearError}>
                <input
                    ref={streetRef}
                    placeholder={data.t.read("supplier_table.street")}
                    className='form-control' type="text"
                    value={updatedSupplier.street ?? ""}
                    onChange={e => {
                        clearError?.('street');
                        setUpdatedSupplier({ ...updatedSupplier, street: e.target.value });
                    }} />
            </ValidatedField>
            <ValidatedField name="city" error={invalidFields?.city} className="col-2 px-0" clearError={clearError}>
                <input
                    ref={cityRef}
                    placeholder={data.t.read("city")}
                    className='form-control' type="text"
                    value={updatedSupplier.city ?? ""}
                    onChange={e => {
                        clearError?.('city');
                        setUpdatedSupplier({ ...updatedSupplier, city: e.target.value });
                    }} />
            </ValidatedField>
            <ValidatedField name="zipcode" error={invalidFields?.zipcode} className="col-1 px-0" clearError={clearError}>
                <input
                    ref={zipcodeRef}
                    placeholder={data.t.read("supplier_table.postal_code_zip")}
                    className='form-control' type="text"
                    value={updatedSupplier.zipcode ?? ""}
                    onChange={e => {
                        clearError?.('zipcode');
                        setUpdatedSupplier({ ...updatedSupplier, zipcode: e.target.value });
                    }} />
            </ValidatedField>
            <ValidatedField name="state" error={invalidFields?.state} className="col-2 px-0" clearError={clearError}>
                <input
                    ref={stateRef}
                    placeholder={data.t.read("state")}
                    className='form-control' type="text"
                    value={updatedSupplier.state ?? ""}
                    onChange={e => {
                        clearError?.('state');
                        setUpdatedSupplier({ ...updatedSupplier, state: e.target.value });
                    }} />
            </ValidatedField>
            <ValidatedField name="country" error={invalidFields?.country} className="col-2 px-0 mb-2" clearError={clearError}>
                <input
                    ref={countryRef}
                    placeholder={data.t.read("country")}
                    className='form-control' type="text"
                    value={updatedSupplier.country ?? ""}
                    onChange={e => {
                        clearError?.('country');
                        setUpdatedSupplier({ ...updatedSupplier, country: e.target.value });
                    }} />
            </ValidatedField>
        </div>
    </div>
);

// A supplier's office is copied onto its invoice lines. On SurePoint a line's office decides
// the company the cost posts to, which stays the invoice's (XPEN-3746), so it is not asked.
export const asksSupplierOffice = (data) =>
    Utils.isEngineType(data, "legal") && !Utils.isExportFormat(data, "surepoint");

const AdditionalFieldsSection = ({ data, updatedSupplier, setUpdatedSupplier, defaultTaxCode, paymentTermsOptions, nxOptions, payeeTypesOptions, payeeCategoriesOptions, loadNxUnits, loadPayeeTypes, loadPayeeCategories, loadOffices, invalidFields, clearError }) => (
    <div className="form-group">
        <h5 className='mt-4'>
            {data.t.read("invoice.additional_fields")}
        </h5>
        <div className='d-flex flex-wrap mt-1'>
            <div className='d-flex mb-2 col-4 pl-0 align-items-center'>
                <div className='col-4 pl-0'>
                    {data.t.read("supplier.tax_code")} <span style={{ color: '#dc3545', fontSize: '12px' }}>*</span>
                </div>
                <div className='col-8'>
                    <ValidatedField name="tax_code" error={invalidFields?.tax_code} clearError={clearError}>
                        <Select
                            name="tax_code"
                            value={updatedSupplier.TaxCode ? { label: updatedSupplier.TaxCode, value: updatedSupplier.TaxCode } : { label: defaultTaxCode, value: defaultTaxCode }}
                            options={(data.config.tax_profile ?? []).map(t => ({
                                label: t.code,
                                value: t.code
                            }))}
                            onChange={choice => {
                                clearError?.('tax_code');
                                setUpdatedSupplier({ ...updatedSupplier, TaxCode: choice.value });
                            }}
                            styles={supplierSelectStyle}
                            classNamePrefix="select"
                            placeholder={data.t.read("supplier.tax_code")} />
                    </ValidatedField>
                </div>
            </div>

            <div className='d-flex mb-2 col-4 pl-0 align-items-center'>
                <div className='col-4 pl-0'>
                    {data.t.read("payment_terms")} <span style={{ color: '#dc3545', fontSize: '12px' }}>*</span>
                </div>
                <div className='col-8'>
                    <ValidatedField name="payment_terms" error={invalidFields?.payment_terms} clearError={clearError}>
                        <Select
                            name="payment_terms"
                            value={(paymentTermsOptions ?? []).find(option => option.value === updatedSupplier.payment_terms)}
                            options={paymentTermsOptions ?? []}
                            onChange={choice => {
                                clearError?.('payment_terms');
                                setUpdatedSupplier({ ...updatedSupplier, payment_terms: choice.value });
                            }}
                            styles={supplierSelectStyle}
                            classNamePrefix="select"
                            placeholder={data.t.read("invoice.payment_terms")} />
                    </ValidatedField>
                </div>
            </div>

            {Utils.isEngineType(data, "legal") &&
                <>
                    <div className='d-flex mb-2 pl-0 col-4 align-items-center'>
                        <div className='col-4 pl-0'>
                            {data.t.read("supplier.is_billable")}
                        </div>
                        <div className='col-8'>
                            <Select
                                name="tax_code"
                                value={updatedSupplier.IsBillable ? { label: data.t.read(`supplier.choices.is_billable.true`), value: true } : { label: data.t.read("supplier.choices.is_billable.false"), value: false }}
                                options={[{ label: data.t.read("supplier.choices.is_billable.true"), value: true }, { label: data.t.read("supplier.choices.is_billable.false"), value: false }]}
                                onChange={choice => setUpdatedSupplier({
                                    ...updatedSupplier,
                                    IsBillable: choice.value
                                })}
                                styles={supplierSelectStyle}
                                classNamePrefix="select"
                                placeholder={data.t.read("supplier.is_billable")} />
                        </div>
                    </div>

                    {asksSupplierOffice(data) && <div className='d-flex mb-2 col-4 pl-0 align-items-center'>
                        <div className='col-4 pl-0'>
                            {data.t.read("office")} <span style={{ color: '#dc3545', fontSize: '12px' }}>*</span>
                        </div>
                        <div className='col-8'>
                            <ValidatedField name="office" error={invalidFields?.office} clearError={clearError}>
                                <AsyncSelect
                                    name="office"
                                    value={updatedSupplier.office ? { label: updatedSupplier.office, value: updatedSupplier.office } : null}
                                    defaultOptions cacheOptions loadOptions={loadOffices}
                                    styles={supplierSelectStyle}
                                    classNamePrefix="select"
                                    onChange={(choice) => {
                                        clearError?.('office');
                                        setUpdatedSupplier({ ...updatedSupplier, office: choice.value });
                                    }}
                                    placeholder={data.t.read("invoice.office")} />
                            </ValidatedField>
                        </div>
                    </div>}
                </>}

            {Utils.isExportFormat(data, "elite") && isGlSegmentActive(data, 'GLOffice') &&
                <div className='d-flex mb-2 col-4 pl-0 align-items-center'>
                    <div className='col-4 pl-0'>
                        {data.t.read("invoice.gl_office")}
                    </div>
                    <div className='col-8'>
                        <GlSegmentSelect
                            segment="GLOffice"
                            value={updatedSupplier.gl_office}
                            onChange={(next) => setUpdatedSupplier({ ...updatedSupplier, gl_office: next?.code ?? next ?? '' })}
                            name="supplier-gl-office-select"
                            placeholder={data.t.read("invoice.gl_office")}
                        />
                    </div>
                </div>}

            {Utils.isExportFormat(data, "elite") && <>
                <div className='d-flex mb-2 col-4 pl-0 align-items-center'>
                    <div className='col-4 pl-0'>
                        {data.t.read("invoice.unit")} <span style={{ color: '#dc3545', fontSize: '12px' }}>*</span>
                    </div>
                    <div className='col-8'>
                        <ValidatedField name="nx_unit" error={invalidFields?.nx_unit} clearError={clearError}>
                            <AsyncSelect
                                name="nx_unit"
                                value={nxOptions.find(option => option.value === updatedSupplier.nx_unit) ?? null}
                                defaultOptions cacheOptions loadOptions={loadNxUnits}
                                styles={supplierSelectStyle}
                                classNamePrefix="select"
                                onChange={(choice) => {
                                    clearError?.('nx_unit');
                                    setUpdatedSupplier({ ...updatedSupplier, nx_unit: choice.value });
                                }}
                                placeholder={data.t.read("invoice.nx_unit")} />
                        </ValidatedField>
                    </div>
                </div>
                <div className='d-flex mb-2 col-4 pl-0 align-items-center'>
                    <div className='col-4 pl-0'>
                        {data.t.read("supplier_table.payee_category")}
                    </div>
                    <div className='col-8'>
                        <AsyncSelect
                            name="payee_category"
                            value={payeeCategoriesOptions.find(option => option.value === updatedSupplier.payee_category) ?? null}
                            defaultOptions cacheOptions loadOptions={loadPayeeCategories}
                            styles={supplierSelectStyle}
                            classNamePrefix="select"
                            onChange={(choice) => setUpdatedSupplier({ ...updatedSupplier, payee_category: choice.value })}
                            placeholder={data.t.read("supplier_table.payee_category")} />
                    </div>
                </div>
                <div className='d-flex mb-2 col-4 pl-0 align-items-center'>
                    <div className='col-4 pl-0'>
                        {data.t.read("supplier_table.payee_type")} <span style={{ color: '#dc3545', fontSize: '12px' }}>*</span>
                    </div>
                    <div className='col-8'>
                        <ValidatedField name="payee_type" error={invalidFields?.payee_type} clearError={clearError}>
                            <AsyncSelect
                                name="payee_type"
                                value={payeeTypesOptions.find(option => option.value === updatedSupplier.payee_type) ?? null}
                                defaultOptions cacheOptions loadOptions={loadPayeeTypes}
                                styles={supplierSelectStyle}
                                classNamePrefix="select"
                                onChange={(choice) => {
                                    clearError?.('payee_type');
                                    setUpdatedSupplier({ ...updatedSupplier, payee_type: choice.value });
                                }}
                                placeholder={data.t.read("supplier_table.payee_type")} />
                        </ValidatedField>
                    </div>
                </div>
                <div className='d-flex mb-2 col-4 pl-0 align-items-center'>
                    <div className='col-4 pl-0'>
                        {data.t.read("supplier_table.tax_number")}
                    </div>
                    <div className='col-8'>
                        <input className='form-control' type="text" value={updatedSupplier.tax_num ?? ""} onChange={e => setUpdatedSupplier({
                            ...updatedSupplier,
                            tax_num: e.target.value
                        })} />
                    </div>
                </div>
            </>}

            {(Utils.isExportFormat(data, "juris_format") || Utils.isExportFormat(data, "elite")) &&
                <div className='d-flex mb-2 col-4 pl-0 align-items-center'>
                    <div className='col-4 pl-0'>
                        {data.t.read("invoice.invoice_ref")}
                    </div>
                    <div className='col-8'>
                        <input className='form-control' type="text" value={updatedSupplier.reference ?? ""} onChange={e => setUpdatedSupplier({
                            ...updatedSupplier,
                            reference: e.target.value
                        })} />
                    </div>
                </div>
            }

            <div className='d-flex mb-2 col-4 pl-0 align-items-center'>
                <div className='col-4 pl-0'>Budget</div>
                <div className="col-8">
                    <MoneyInput
                        value={updatedSupplier.budget}
                        onChange={val => setUpdatedSupplier({
                            ...updatedSupplier,
                            budget: val
                        })}
                        className="form-control"
                        locale={`${data.t.lang}-${data.t.lang.toUpperCase()}`}
                        placeholder="budget" />
                </div>
            </div>
        </div>
    </div>
);

const ContactInfoField = ({ data, updatedSupplier, setUpdatedSupplier, emailRef, firstNameRef, lastNameRef }) => (
    <div className="form-group">
        <h5 className='mt-4'>
            {data.t.read("contact_info")}
        </h5>
        {updatedSupplier?.entity_type?.toLowerCase() === "entityperson" &&
            <div className='mt-1 d-flex mb-2 justify-content-between'>
                <input placeholder={data.t.read("first_name")}
                    required
                    ref={firstNameRef}
                    className='form-control mr-1'
                    value={updatedSupplier.first_name ?? ""}
                    onChange={e => setUpdatedSupplier({
                        ...updatedSupplier,
                        first_name: e.target.value
                    })} />
                <input placeholder={data.t.read("middle_name")}
                    className='form-control mr-1'
                    value={updatedSupplier.middle_name ?? ""}
                    onChange={e => setUpdatedSupplier({
                        ...updatedSupplier,
                        middle_name: e.target.value
                    })} />
                <input placeholder={data.t.read("last_name")}
                    required
                    ref={lastNameRef}
                    className='form-control'
                    value={updatedSupplier.last_name ?? ""}
                    onChange={e => setUpdatedSupplier({
                        ...updatedSupplier,
                        last_name: e.target.value
                    })} />
            </div>}
        <div className='mt-1 d-flex justify-content-between'>
            <input ref={emailRef} placeholder={data.t.read("email")}
                className='form-control mr-1'
                type="email"
                value={updatedSupplier.email ?? ""}
                onChange={e => setUpdatedSupplier({
                    ...updatedSupplier,
                    email: e.target.value
                })} />
            <input placeholder={data.t.read("invoice.supplier_phone")}
                className='form-control mr-1'
                type="text"
                value={updatedSupplier.phone_number ?? ""}
                onChange={e => setUpdatedSupplier({
                    ...updatedSupplier,
                    phone_number: e.target.value
                })} />
            <input placeholder={"URL"}
                className='form-control mr-1'
                type="text"
                value={updatedSupplier.url ?? ""}
                onChange={e => setUpdatedSupplier({
                    ...updatedSupplier,
                    url: e.target.value
                })} />
        </div>
    </div>
);

const BankInfoField = ({ data, updatedSupplier, setUpdatedSupplier, bankAccountTypesOptions, loadBankAccountTypes, bankDescriptionRef, bankNumberRef, branchNumberRef, accountNumberRef, invalidFields, clearError }) => {
    
    return (
        <div className='form-group'>
            <h5 className='mt-4'>{data.t.read("bank")}</h5>
            <div className='d-flex flex-wrap'>
                <div className='d-flex mb-2 col-4 p-0 align-items-center'>
                    <div className='col-4 p-0'>
                        {data.t.read("description")}
                    </div>
                    <div className='col-8'>
                        <input className='form-control'
                            ref={bankDescriptionRef}
                            type="text" value={updatedSupplier.bank?.description ?? ""} onChange={e => setUpdatedSupplier({
                                ...updatedSupplier,
                                bank: {
                                    ...updatedSupplier.bank ?? {},
                                    description: e.target.value
                                }
                            })} />
                    </div>
                </div>
                <div className='d-flex mb-2 col-4 p-0 align-items-center'>
                    <div className='col-4 p-0'>
                        {data.t.read("bank_account_type")}
                    </div>
                    <div className='col-8'>
                        <AsyncSelect
                            name="bank_account_type"
                            value={bankAccountTypesOptions.find(option => option.value === updatedSupplier.bank?.bank_account_type) ?? null}
                            defaultOptions cacheOptions loadOptions={loadBankAccountTypes}
                            styles={supplierSelectStyle}
                            classNamePrefix="select"
                            onChange={(choice) => setUpdatedSupplier({ ...updatedSupplier, bank: { ...updatedSupplier.bank, bank_account_type: choice.value } })}
                            placeholder={data.t.read("bank_account_type")} />
                    </div>
                </div>
                <div className='d-flex mb-2 col-4 p-0 align-items-center'>
                    <div className='col-4 p-0'>
                        {data.t.read("bank_number")}
                    </div>
                    <div className='col-8'>
                        <input className='form-control'
                            ref={bankNumberRef}
                            maxLength="3" value={updatedSupplier.bank?.bank_number ?? ""} onChange={e => setUpdatedSupplier({
                                ...updatedSupplier,
                                bank: {
                                    ...updatedSupplier.bank ?? {},
                                    bank_number: e.target.value
                                }
                            })} />
                    </div>
                </div>
                <div className='d-flex mb-2 col-4 p-0 align-items-center'>
                    <div className='col-4 p-0'>
                        {data.t.read("branch_number")}
                    </div>
                    <div className='col-8'>
                        <ValidatedField name="branch_number" error={invalidFields?.branch_number} clearError={clearError}>
                            <input className='form-control' ref={branchNumberRef} maxLength="5" value={updatedSupplier.bank?.branch_number ?? ""}
                                onChange={e => {
                                    const val = e.target.value.replace(/\D/g, '');
                                    clearError?.('branch_number');
                                    setUpdatedSupplier({ ...updatedSupplier, bank: { ...updatedSupplier.bank ?? {}, branch_number: val } });
                                }} />
                        </ValidatedField>
                    </div>
                </div>
                <div className='d-flex mb-2 col-4 p-0 align-items-center'>
                    <div className='col-4 p-0'>
                        {data.t.read("account_number")}
                    </div>
                    <div className='col-8'>
                        <input className='form-control' ref={accountNumberRef} value={updatedSupplier.bank?.account_number ?? ""} onChange={e => setUpdatedSupplier({
                            ...updatedSupplier,
                            bank: {
                                ...updatedSupplier.bank ?? {},
                                account_number: e.target.value
                            }
                        })} />
                    </div>
                </div>
            </div>
        </div>
    );
};

const OtherInfoField = ({ data, updatedSupplier, setUpdatedSupplier, groupOptions }) => (
    <div className='form-group'>
        <h5 className='mt-2'>{data.t.read("supplier_table.other_information")}</h5>
        <div className='d-flex flex-wrap'>
            <div className='d-flex mb-2 col-4 p-0 align-items-center'>
                <div className='col-8 p-0'>
                    {data.t.read("supplier_table.active")}
                </div>
                <div className='col-4'>
                    <Switch className='switch'
                        checked={updatedSupplier.active ?? true}
                        width={65}
                        onColor="#F39800"
                        onHandleColor="#2693e6"
                        boxShadow="0px 1px 5px rgba(0, 0, 0, 0.6)"
                        activeBoxShadow="0px 0px 1px 10px rgba(0, 0, 0, 0.2)"
                        onChange={active => setUpdatedSupplier({
                            ...updatedSupplier,
                            active: active
                        })} />
                </div>
            </div>

            <div className='d-flex mb-2 col-4 p-0 align-items-center'>
                <div className='col-8 p-0'>
                    {data.t.read("supplier_table.needs_moderation")}
                </div>
                <div className='col-4'>
                    <Switch className='switch'
                        checked={updatedSupplier.need_moderation ?? false}
                        width={65}
                        onColor="#F39800"
                        onHandleColor="#2693e6"
                        boxShadow="0px 1px 5px rgba(0, 0, 0, 0.6)"
                        activeBoxShadow="0px 0px 1px 10px rgba(0, 0, 0, 0.2)"
                        onChange={(need_moderation) => setUpdatedSupplier({
                            ...updatedSupplier,
                            need_moderation: need_moderation
                        })} />
                </div>
            </div>
            {data?.config?.supplier_group &&
                <div className='d-flex mb-2 col-4 p-0 align-items-center'>
                    <Select
                        name="group"
                        value={groupOptions.find(option => option.value === updatedSupplier.supplier_group) ?? null}
                        options={groupOptions}
                        styles={supplierSelectStyle}
                        classNamePrefix="select"
                        isClearable
                        className="col p-0"
                        menuPlacement="top"
                        onChange={(choice) => setUpdatedSupplier({ ...updatedSupplier, supplier_group: choice?.value ?? null })}
                        placeholder={data.t.read("supplier_group")} />
                </div>
            }
        </div>
    </div>
);


// Field configuration - controls which fields are displayed
const getFieldConfiguration = (data, showFields = {}) => {
    const defaultConfig = {
        entityType: true,
        names: true,
        codes: true,
        address: true,
        additionalFields: true,
        contactInfo: true,
        bankInfo: true,
        otherInfo: true,
        ...showFields
    };

    // Override based on data configuration
    return {
        ...defaultConfig,
        entityType: defaultConfig.entityType && Utils.isExportFormat(data),
        // XPEN-3607: a tenant whose policy keeps vendor bank details out of XPENS
        // never shows the section, whatever the format and the role.
        bankInfo: defaultConfig.bankInfo && data.config?.hide_supplier_bank_info !== true && Utils.isExportFormat(data,"elite") && Utils.userHasRole(data, ["SuperAdmin", "Admin", "Manager"]),
        codes: Utils.userHasRole(data, ["SuperAdmin", "Admin", "Manager", "Clerk"])
    };
};

const getSimplifiedConfig = () => {
    const configs = {
        names: true,
        address: true,
    }
    
    return configs
}

const SupplierForm = ({ 
    supplier = {}, 
    cancelSupplierModal, 
    data, 
    setShowSupplierModal, 
    callerCallback,
    showFields = {}, // New prop to control field visibility
    simplifiedForm = false,
    origin_invoice_id = null
}) => {
    // XPEN-3631: the firm's own terms, falling back to the fixed choices while
    // they load and for a firm that has none synced.
    const [firmPaymentTerms, setFirmPaymentTerms] = useState([]);
    const defaultApGlNat = useMemo(
        () => data?.config?.ap_gl_natural ??
            null,
        [data]
    );
    // Neutralised here rather than guarded at each use: the default also feeds
    // the initial state, an effect and the submit path, so a tenant that hides
    // the field would otherwise keep posting the configured sentinel.
    const defaultExpenseGlNat = useMemo(
        () => (data?.config?.hide_supplier_expense_gl ? null : data?.config?.expense_gl_natural ?? null),
        [data]
    );
    const initialDefaultTaxCode = resolveDefaultTaxCode(data.config?.tax_profile);
    const [defaultTaxCode] = useState(initialDefaultTaxCode);
    const [updatedSupplier, setUpdatedSupplier] = useState({
        ...supplier,
        ap_gl_nat: supplier?.ap_gl_nat ?? defaultApGlNat ?? null,
        expense_gl_nat: supplier?.expense_gl_nat ?? defaultExpenseGlNat ?? null,
        TaxCode: supplier?.TaxCode ?? supplier?.tax_code ?? initialDefaultTaxCode,
    });
    const paymentTermsOptions = useMemo(
        () => buildPaymentTermsOptions(firmPaymentTerms, data, updatedSupplier?.payment_terms),
        [firmPaymentTerms, data, updatedSupplier?.payment_terms]);
    const [invalidFields, setInvalidFields] = useState({});
    const fieldConfig = !simplifiedForm ? getFieldConfiguration(data, showFields) : getSimplifiedConfig();
    const [errorMessage, setErrorMessage] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [loading, setLoading] = useState(false);
    const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);
    const [nxOptions, setNxOptions] = useState([]);
    const [payeeTypesOptions, setPayeeTypesOptions] = useState([]);
    const [payeeCategoriesOptions, setPayeeCategoriesOptions] = useState([]);
    const [bankAccountTypesOptions, setBankAccountTypesOptions] = useState([]);
    const entityOptions = useMemo(() => Utils.getEntityOptions(data), [data]);

    // XPEN-3631: the firm's payment terms. Paged, and accumulated on
    // `meta.totalRowCount` rather than on the first page: a firm holds five to
    // fifteen today, which is exactly the assumption that has broken before.
    useEffect(() => {
        let abandoned = false;
        const collected = [];
        const readPage = (start) => {
            cachedFetch(`/suppliers/payment-terms?start=${start}&page_size=100`, "GET", {},
                (response) => {
                    if (abandoned) return;
                    // `cachedFetch` hands back `{data}` on a cache hit and the
                    // axios response on a miss; the body is under `data` either way.
                    const page = response?.data?.payment_terms ?? [];
                    collected.push(...page);
                    const total = response?.data?.meta?.totalRowCount ?? collected.length;
                    if (page.length > 0 && collected.length < total) {
                        readPage(collected.length);
                        return;
                    }
                    setFirmPaymentTerms(collected);
                },
                // A firm with none, or an older deployment without the route:
                // the fixed choices stay, which is what the form did before.
                () => { if (!abandoned) setFirmPaymentTerms([]); });
        };
        readPage(0);
        return () => { abandoned = true; };
    }, []);

    // XPEN-3635: a new supplier starts on the firm's default term rather than on
    // an empty required field. Only on create, only while the field is still
    // untouched, and never for a supplier that already carries one.
    useEffect(() => {
        if (supplier?._id) return;
        const firmDefault = defaultPaymentTerm(firmPaymentTerms);
        if (firmDefault === null) return;
        setUpdatedSupplier((prev) => (
            prev.payment_terms == null ? { ...prev, payment_terms: firmDefault } : prev));
    }, [firmPaymentTerms, supplier?._id]);

    useEffect(() => {
        if (!updatedSupplier?.ap_gl_nat && defaultApGlNat) {
            setUpdatedSupplier((prev) => ({ ...prev, ap_gl_nat: defaultApGlNat }));
        }
    }, [defaultApGlNat, updatedSupplier?.ap_gl_nat]);

    useEffect(() => {
        if (!updatedSupplier?.expense_gl_nat && defaultExpenseGlNat) {
            setUpdatedSupplier((prev) => ({ ...prev, expense_gl_nat: defaultExpenseGlNat }));
        }
    }, [defaultExpenseGlNat, updatedSupplier?.expense_gl_nat]);

    const loadNxUnits = (search_query = "", callback = () => {
    }) => {
        const val = search_query.trim().replace(/\//g, '');
        cachedFetch(`/suppliers/unit/search/${val}`, "GET", {}, (response) => {
            const allNxOptions = response.data.map(u => ({
                label: u.name,
                value: u.unit
            }));
            if (nxOptions?.length === 0) {
                setNxOptions(allNxOptions);
            }
            callback(allNxOptions?.slice(0, 10))
        })
    }

    const loadPayeeTypes = (search_query = "", callback = () => {
    }) => {
        const val = search_query.trim().replace(/\//g, '');
        const filters = val.length > 0 ? [{ id: "Description", value: val }] : [];
        const searchData = `?field_name=PayeeType&filters=${encodeURIComponent(JSON.stringify(filters ?? []))}`;
        cachedFetch(`/client/options/${searchData}`, "GET", {}, (response) => {
            const allPayeeTypes = response.data.map(p => ({
                label: p.Description,
                value: p.Code
            }));
            if (payeeTypesOptions?.length === 0) {
                setPayeeTypesOptions(allPayeeTypes)
            }
            callback(allPayeeTypes?.slice(0, 10))
        })
    }

    const loadPayeeCategories = (search_query = "", callback = () => {
    }) => {
        const val = search_query.trim().replace(/\//g, '');
        const filters = val.length > 0 ? [{ id: "Description", value: val }] : [];
        const searchData = `?field_name=PayeeCategory&filters=${encodeURIComponent(JSON.stringify(filters ?? []))}`;
        cachedFetch(`/client/options/${searchData}`, "GET", {}, (response) => {
            const allPayeeCategories = response.data.map(p => ({
                label: p.Description,
                value: p.Code
            }));
            if (payeeCategoriesOptions?.length === 0) {
                setPayeeCategoriesOptions(allPayeeCategories)
            }
            callback(allPayeeCategories?.slice(0, 10))
        })
    }

    const loadBankAccountTypes = (search_query = "", callback = () => {
    }) => {
        const val = search_query.trim().replace(/\//g, '');
        const filters = val.length > 0 ? [{ id: "Description", value: val }] : [];
        const searchData = `?field_name=BankAcctType&filters=${encodeURIComponent(JSON.stringify(filters ?? []))}`;
        cachedFetch(`/client/options/${searchData}`, "GET", {}, (response) => {
            const allBankAccountTypes = response.data.map(p => ({
                label: p.Description,
                value: p.Code
            }));
            if (bankAccountTypesOptions?.length === 0) {
                setBankAccountTypesOptions(allBankAccountTypes)
            }
            callback(allBankAccountTypes?.slice(0, 10))
        })
    }

    const loadOffices = (search_query = "", callback = () => {
    }) => {
        const val = search_query.trim().replace(/\//g, '');
        const filters = [{ id: "search", value: val }, { id: "id_client", value: data.id_client }];
        const sorting = [{ id: "name", desc: false }];
        const searchData = `?filters=${encodeURIComponent(JSON.stringify(filters ?? []))}&sorting=${encodeURIComponent(JSON.stringify(sorting ?? []))}`;
        cachedFetch(`/offices/${searchData}`, "GET", {}, (response) => {
            callback(response.data.offices.map(o => ({
                label: o.name,
                value: o.name
            })))
        })
    }

    const handleAddressChange = (val) => {
        if (val && val.place_id) {
            fetch(`/places/details/?place_id=${val.place_id}`, "GET", {},
                response =>
                    setUpdatedSupplier({
                        ...updatedSupplier,
                        street: `${response.data.street_number ?? ""} ${response.data.street_name ?? ""}` ?? updatedSupplier.street,
                        address: response.data.address ?? updatedSupplier.address,
                        zipcode: response.data.zipcode ?? updatedSupplier.zipcode,
                        city: response.data.city ?? updatedSupplier.city,
                        state: response.data.state ?? updatedSupplier.state,
                        country: response.data.country ?? updatedSupplier.country
                    }),
                null, true
            )
        }
        else {
            setUpdatedSupplier({
                ...updatedSupplier,
                address: val?.name ?? val
            })
        }

    }

    const groupOptions = useMemo(() => Utils.getGroupOptions(data), [data]);


    // Refs pour focus automatique
    const formRef = useRef(null);
    const nameRefs = useRef([]);
    const streetRef = useRef(null);
    const cityRef = useRef(null);
    const zipcodeRef = useRef(null);
    const stateRef = useRef(null);
    const countryRef = useRef(null);
    const emailRef = useRef(null);
    const firstNameRef = useRef(null);
    const lastNameRef = useRef(null);
    const supplierNumberRef = useRef(null);
    const bankNumberRef = useRef(null);
    const branchNumberRef = useRef(null);
    const accountNumberRef = useRef(null);
    const bankDescriptionRef = useRef(null);

    const clearError = (fieldName) =>
        setInvalidFields(prev => {
            if (!prev[fieldName]) return prev;
            const next = { ...prev };
            delete next[fieldName];
            return next;
        });

    const shouldFocusRef = useRef(false);

    // Focus the first invalid field in DOM order after a submit attempt.
    useEffect(() => {
        if (!shouldFocusRef.current || !formRef.current || Object.keys(invalidFields).length === 0) return;
        shouldFocusRef.current = false;
        const firstErrorEl = Array.from(formRef.current.querySelectorAll('[data-field]'))
            .find(el => invalidFields[el.getAttribute('data-field')]);
        if (!firstErrorEl) return;
        firstErrorEl.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' });
        const focusTarget =
            firstErrorEl.querySelector('.select__input-container input') ||
            firstErrorEl.querySelector('input[type="text"]') ||
            firstErrorEl.querySelector('input');
        focusTarget?.focus();
    }, [invalidFields]);

    const handleSupplierSubmit = () => {
        const REQUIRED = data.t.read("field_required");
        const errors = {};

        if (fieldConfig.names) {
            if (!updatedSupplier.names?.length) {
                errors.names = REQUIRED;
            } else {
                updatedSupplier.names.forEach((n, i) => {
                    if (!n.trim()) errors[`names.${i}`] = REQUIRED;
                });
            }
        }

        const isCreateMode = !updatedSupplier?._id;
        if (fieldConfig.codes) {
            const supplierNumberReadOnly = Utils.isExportFormat(data, "juris_format") || !!data.config?.auto_supplier_number;
            if (!supplierNumberReadOnly && !updatedSupplier.supplier_number)
                errors.supplier_number = REQUIRED;
            if (!updatedSupplier.IsBillable && !updatedSupplier.gl_code)
                errors.gl_code = REQUIRED;
            if (Utils.isEngineType(data, "legal") && updatedSupplier.IsBillable && !updatedSupplier.cost_type)
                errors.cost_type = REQUIRED;
            if (Utils.isExportFormat(data, "elite")) {
                const vendorNumberReadOnly = !isCreateMode || !!data.config?.auto_supplier_number;
                if (!vendorNumberReadOnly && !updatedSupplier.vendor_number) errors.vendor_number = REQUIRED;
                if (!data.config?.hide_supplier_expense_gl && !updatedSupplier.expense_gl_nat) errors.expense_gl_nat = REQUIRED;
            }
        }

        if (fieldConfig.address) {
            // Juris pushes the supplier to the ERP, which requires a complete address;
            // flag the empty ones here so the user gets a localized per-field message
            // instead of a generic backend rejection (XPEN-3215).
            if (Utils.isExportFormat(data, "juris_format")) {
                if (!updatedSupplier.address) errors.address = REQUIRED;
                if (!updatedSupplier.city) errors.city = REQUIRED;
                if (!updatedSupplier.state) errors.state = REQUIRED;
                if (!updatedSupplier.country) errors.country = REQUIRED;
                if (!updatedSupplier.zipcode) errors.zipcode = REQUIRED;
            }
        }

        const branchNumber = updatedSupplier.bank?.branch_number;
        if (branchNumber && !/^\d{5}$/.test(branchNumber)) {
            errors.branch_number = data.t.read("branch_number_invalid") !== "branch_number_invalid"
                ? data.t.read("branch_number_invalid")
                : "Must be exactly 5 digits";
        }

        if (fieldConfig.additionalFields) {
            if (!(updatedSupplier.TaxCode || defaultTaxCode)) errors.tax_code = REQUIRED;
            if (updatedSupplier.payment_terms == null) errors.payment_terms = REQUIRED;
            if (asksSupplierOffice(data) && !updatedSupplier.office) errors.office = REQUIRED;
            if (Utils.isExportFormat(data, "elite")) {
                if (!updatedSupplier.nx_unit) errors.nx_unit = REQUIRED;
                if (!updatedSupplier.payee_type) errors.payee_type = REQUIRED;
            }
        }

        if (Object.keys(errors).length > 0) {
            const firstKey = Object.keys(errors)[0];
            shouldFocusRef.current = true;
            setInvalidFields({ [firstKey]: errors[firstKey] });
            return false;
        }

        setInvalidFields({});
        if (updatedSupplier._id) {
            updateSupplier();
        } else {
            // Propagates the governance `false` so Modal.processOk re-enables its
            // buttons instead of leaving the form on "please wait" (XPEN-3267).
            return createSupplier();
        }
    };

    const createSupplier = (newSupplier = {...updatedSupplier}) => {
        // XPEN-3267 — last guard before the write. The callers that open this
        // form already check, and the API rejects the POST regardless; this is
        // here so a future caller inherits the block instead of hitting a 403.
        // Returns false like the validation failures above so the modal releases
        // its buttons — nothing will flip `loading` back for it here.
        if (!Utils.clientAllowsSupplierCreate(data)) {
            setErrorMessage(data.t.read("supplier_creation_disabled"));
            return false;
        }

        setLoading(true);
        if (!newSupplier.ap_gl_nat && defaultApGlNat) {
            newSupplier.ap_gl_nat = defaultApGlNat;
        }
        if (!newSupplier.expense_gl_nat && defaultExpenseGlNat) {
            newSupplier.expense_gl_nat = defaultExpenseGlNat;
        }
        if (!newSupplier.TaxCode && defaultTaxCode) {
            newSupplier.TaxCode = defaultTaxCode;
        }
        if(simplifiedForm === true){
            newSupplier.partial = true;
            newSupplier.origin_invoice_id = origin_invoice_id;
        }
        
        if (data.config?.hide_supplier_bank_info === true) delete newSupplier.bank;
        fetch("/suppliers/", "POST", newSupplier,
            (response) => {
                setLoading(false);
                setShowSupplierModal(false);
                newSupplier._id = response.data.id;
                callerCallback(newSupplier);
            },
            (error) => {
                supplier = { ...newSupplier };
                if (error?.response?.data?.msg) {
                    // The API sends a translation key for errors it defines
                    // (e.g. supplier_creation_disabled) and a plain sentence for
                    // the rest; `t.read` returns undefined for anything unknown.
                    const msg = error.response.data.msg;
                    setErrorMessage(data.t.read(msg) || msg);
                } else {
                    setErrorMessage(data.t.read("failed_to_create_supplier"));
                }
                setLoading(false);
            }
        );
    };

    const updateSupplier = () => {
        const supplierId = updatedSupplier._id?.$oid;
        setLoading(true);
        const payload = { ...updatedSupplier };
        if (!payload.TaxCode && defaultTaxCode) {
            payload.TaxCode = defaultTaxCode;
        }
        if (data.config?.hide_supplier_bank_info === true) delete payload.bank;
        fetch(`/suppliers/${supplierId}`, "PUT", payload,

            () => {
                setLoading(false);
                setShowSupplierModal(false);
                callerCallback(payload);
            },
            (error) => {
                setLoading(false);
                if (error.msg) {
                    setErrorMessage(error.msg);
                }
                else if (error.response?.data?.msg) {
                    setErrorMessage(error.response.data.msg);
                }
                else {
                    setErrorMessage(data.t.read("failed_to_update_supplier"));
                }
            }
        );
    };


    return <form method="post" ref={formRef}>
        <Modal
            width="60vw"
            title={updatedSupplier?.names?.[0] || data.t.read("new_supplier")}
            okAction={handleSupplierSubmit} // only for admin and manager
            okText={simplifiedForm ? data.t.read("save") : AccessControl.userCanExportSuppliers(data) ? data.t.read("finalize") : data.t.read("save")}
            cancelAction={() => setShowCancelConfirmation(true)}
            cancelText={data.t.read("cancel")}
            finishProcessing={loading}
            disableBackdropClose={true}
        >

            {loading && <div className="overlay d-flex justify-content-center align-items-center w-100">
                <FontAwesomeIcon className="infinite-rotate" icon={faSpinner} />
            </div>}

            {errorMessage && <Modal title="Attention" okText={"OK"} okAction={() => setErrorMessage(null)}>
                <p style={{ whiteSpace: 'pre-line' }} className='text-danger'>  {errorMessage}</p>
            </Modal>
            }
            {successMessage && <Modal title="Attention" okText={"OK"} okAction={() => setSuccessMessage(null)}>
                {successMessage}
            </Modal>
            }

            {/* Conditionally render fields based on configuration */}
            {fieldConfig.entityType && (
                <EntityTypeField 
                    data={data}
                    updatedSupplier={updatedSupplier}
                    setUpdatedSupplier={setUpdatedSupplier}
                    entityOptions={entityOptions}
                />
            )}

            {fieldConfig.names && (
                <NamesField
                    data={data}
                    updatedSupplier={updatedSupplier}
                    setUpdatedSupplier={setUpdatedSupplier}
                    nameRefs={nameRefs}
                    invalidFields={invalidFields}
                    clearError={clearError}
                />
            )}

            {fieldConfig.codes && (
                <CodesField
                    data={data}
                    updatedSupplier={updatedSupplier}
                    setUpdatedSupplier={setUpdatedSupplier}
                    supplierNumberRef={supplierNumberRef}
                    defaultApGlNat={defaultApGlNat}
                    defaultExpenseGlNat={defaultExpenseGlNat}
                    isCreateMode={!updatedSupplier?._id}
                    invalidFields={invalidFields}
                    clearError={clearError}
                />
            )}

            {fieldConfig.address && (
                <AddressField
                    data={data}
                    updatedSupplier={updatedSupplier}
                    setUpdatedSupplier={setUpdatedSupplier}
                    handleAddressChange={handleAddressChange}
                    streetRef={streetRef}
                    cityRef={cityRef}
                    zipcodeRef={zipcodeRef}
                    stateRef={stateRef}
                    countryRef={countryRef}
                    invalidFields={invalidFields}
                    clearError={clearError}
                />
            )}

            {fieldConfig.additionalFields && (
                <AdditionalFieldsSection
                    data={data}
                    updatedSupplier={updatedSupplier}
                    setUpdatedSupplier={setUpdatedSupplier}
                    defaultTaxCode={defaultTaxCode}
                    paymentTermsOptions={paymentTermsOptions}
                    nxOptions={nxOptions}
                    payeeTypesOptions={payeeTypesOptions}
                    payeeCategoriesOptions={payeeCategoriesOptions}
                    loadNxUnits={loadNxUnits}
                    loadPayeeTypes={loadPayeeTypes}
                    loadPayeeCategories={loadPayeeCategories}
                    loadOffices={loadOffices}
                    invalidFields={invalidFields}
                    clearError={clearError}
                />
            )}

            {fieldConfig.contactInfo && (
                <ContactInfoField 
                    data={data}
                    updatedSupplier={updatedSupplier}
                    setUpdatedSupplier={setUpdatedSupplier}
                    emailRef={emailRef}
                    firstNameRef={firstNameRef}
                    lastNameRef={lastNameRef}
                />
            )}

            {fieldConfig.bankInfo && (
                <BankInfoField 
                    data={data}
                    updatedSupplier={updatedSupplier}
                    setUpdatedSupplier={setUpdatedSupplier}
                    bankAccountTypesOptions={bankAccountTypesOptions}
                    loadBankAccountTypes={loadBankAccountTypes}
                    bankDescriptionRef={bankDescriptionRef}
                    bankNumberRef={bankNumberRef}
                    branchNumberRef={branchNumberRef}
                    accountNumberRef={accountNumberRef}
                    invalidFields={invalidFields}
                    clearError={clearError}
                />
            )}

            {fieldConfig.otherInfo && (
                <OtherInfoField 
                    data={data}
                    updatedSupplier={updatedSupplier}
                    setUpdatedSupplier={setUpdatedSupplier}
                    groupOptions={groupOptions}
                />
            )}
        </Modal>
        {showCancelConfirmation && (
            <Modal
                title={data.t.read("confirm")}
                okAction={() => {
                    setShowCancelConfirmation(false);
                    cancelSupplierModal();
                }}
                cancelAction={() => setShowCancelConfirmation(false)}
                okText={data.t.read("yes")}
                cancelText={data.t.read("no")}
            >
                {data.t.read("supplier_table.cancel_confirmation")}
            </Modal>
        )}
    </form>
}

export default inject('data')(observer(SupplierForm));
